/**
 * A parsed answer, drawn on the components v1.5 already has for it.
 *
 * `renderAnswer` returns an ARRAY rather than a fragment, and that is not a style preference. The
 * turn's assistant slot is a flex column with `gap: 12px` — the only gap in v1.5 — so its children
 * have to be the blocks themselves. A fragment collapses a heading, a table and three steps into one
 * flex item and takes all the rhythm out of the answer it just recovered.
 *
 * Which component draws what is settled in `markdown.ts`; this file only wires it. The one thing
 * decided here is that anything without a component is handed to `AssistantProse`, because prose is
 * what it was before it was parsed and prose is the honest place for it to stay.
 */
import type { ReactNode } from 'react';
import {
  AnswerHeading,
  AssistantProse,
  DataTable,
  OrderedList,
  type AssistantProseProps,
  type ChatTheme,
} from '~/design-system/components/v1_5/Chat';
import { parseAnswer, tableColumns, type InlineRun, type ProseNode } from './markdown';
import styles from './RichProse.module.css';

/**
 * How loud the answer is: `default` for the agent's reply, `subtle` for a work note.
 *
 * Derived from the prop rather than restated, so the two can never come to mean different things.
 * It reaches only the blocks whose tone is not already decided — `AnswerHeading` is `--cv15-text`
 * in every artboard, and `OrderedList` and `DataTable` each carry their own ramp — so in practice
 * this is the paragraph, the bulleted list and the fenced block.
 */
export type AnswerTone = NonNullable<AssistantProseProps['tone']>;

/** The runs of one paragraph, list item or step. */
function Inline({ runs, keyPrefix }: { runs: InlineRun[]; keyPrefix: string }): ReactNode {
  return runs.map((run, at) => {
    const key = `${keyPrefix}-${at}`;
    switch (run.kind) {
      case 'bold':
        return (
          <strong key={key} className={styles.bold}>
            {run.text}
          </strong>
        );
      case 'italic':
        return (
          <em key={key} className={styles.italic}>
            {run.text}
          </em>
        );
      case 'code':
        return (
          <code key={key} className={styles.code}>
            {run.text}
          </code>
        );
      case 'link':
        return (
          // `noopener` with `noreferrer`: the agent writes these hrefs from search results, so they
          // are as untrusted as anything else that arrived over the wire.
          <a
            key={key}
            className={styles.link}
            href={run.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {run.text}
          </a>
        );
      default:
        return <span key={key}>{run.text}</span>;
    }
  });
}

/** One parsed block → the component that draws it. */
function nodeToElement(node: ProseNode, key: string, theme: ChatTheme, tone: AnswerTone): ReactNode {
  switch (node.kind) {
    case 'heading':
      return (
        <AnswerHeading key={key} theme={theme}>
          {node.text}
        </AnswerHeading>
      );

    case 'steps':
      return (
        <OrderedList
          key={key}
          theme={theme}
          items={node.items.map((runs, at) => <Inline key={at} runs={runs} keyPrefix={`${key}-${at}`} />)}
        />
      );

    case 'table':
      return (
        <DataTable
          key={key}
          theme={theme}
          columns={tableColumns(node.columns, node.rows)}
          rows={node.rows}
          // The sentence introducing a table is a sibling paragraph, not part of the component, so
          // the accessible name is the header row read out — the only thing the table itself knows.
          label={node.columns.filter(Boolean).join(', ') || undefined}
        />
      );

    case 'bullets':
      return (
        <AssistantProse key={key} theme={theme} tone={tone}>
          <div className={styles.bullets}>
            {node.items.map((runs, at) => (
              <div key={at} className={styles.bullet}>
                {/* Hidden from assistive tech for the reason `OrderedList` hides its numeral: the
                    mark is scaffolding, and a screen reader reading "bullet, bullet, bullet" is
                    reading the layout aloud. There is no `<ul>` here to carry the semantics
                    instead, so the items simply read as the sentences they are. */}
                <span className={styles.marker} aria-hidden="true">
                  •
                </span>
                <span className={styles.bulletBody}>
                  <Inline runs={runs} keyPrefix={`${key}-${at}`} />
                </span>
              </div>
            ))}
          </div>
        </AssistantProse>
      );

    case 'code':
      return (
        <AssistantProse key={key} theme={theme} tone={tone}>
          <code className={styles.codeBlock}>{node.text}</code>
        </AssistantProse>
      );

    default:
      return (
        <AssistantProse key={key} theme={theme} tone={tone}>
          <Inline runs={node.runs} keyPrefix={key} />
        </AssistantProse>
      );
  }
}

/**
 * One assistant answer → the blocks of the turn it should become.
 *
 * `keyPrefix` is the block's id, so a turn holding two prose blocks cannot collide.
 */
export function renderAnswer(
  text: string,
  keyPrefix: string,
  theme: ChatTheme,
  tone: AnswerTone = 'subtle',
): ReactNode[] {
  return parseAnswer(text).map((node, at) => nodeToElement(node, `${keyPrefix}-${at}`, theme, tone));
}
